import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Gift, ArrowRight, Star, Trophy, Copy, ExternalLink } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Link } from "react-router-dom";

export const ReferFriend = () => {
  const [referrals, setReferrals] = useState(2); // Mock current referrals
  const maxReferrals = 5;
  const progress = (referrals / maxReferrals) * 100;

  useEffect(() => {
    if (referrals >= maxReferrals) {
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      const interval: any = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
      }, 250);
    }
  }, [referrals]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText("https://uplevelcleaning.com/ref/user123");
    toast.success("Referral link copied to clipboard!");
  };

  return (
    <section className="py-20 bg-muted/30 dark:bg-slate-900/20">
      <div className="container px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden border-none shadow-xl bg-white dark:bg-slate-800">
            <div className="grid md:grid-cols-2">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary dark:text-primary text-sm font-semibold mb-6 w-fit">
                  <Users className="w-4 h-4" />
                  <span>Referral Program</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground dark:text-white">
                  Share the sparkle, <span className="text-primary dark:text-primary">get rewarded.</span>
                </h2>
                <p className="text-muted-foreground dark:text-slate-300 text-lg mb-8">
                  Love your clean home? Refer a friend to Uplevel Cleaning and you'll both receive a $25 discount on your next service. There's no limit to how much you can earn!
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-primary text-primary-foreground dark:text-white hover:bg-primary-hover font-semibold"
                    onClick={handleCopyLink}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Referral Link
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="font-semibold dark:border-slate-600 dark:text-white dark:hover:bg-slate-700"
                    asChild
                  >
                    <Link to="/dashboard/referrals">
                      Track My Referrals
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="relative bg-primary dark:bg-slate-900 hidden md:flex flex-col items-center justify-center p-10 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path d="M0 0 L100 100 M100 0 L0 100" stroke="currentColor" strokeWidth="0.5" className="text-white" />
                  </svg>
                </div>
                <div className="relative z-10 w-full max-w-sm text-center text-white">
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 mb-6 mx-auto inline-flex items-center justify-center">
                    <Trophy className="w-12 h-12 text-white dark:text-primary" />
                  </div>
                  
                  <div className="space-y-4 bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 shadow-xl">
                    <div className="flex justify-between items-end">
                      <div className="text-left">
                        <p className="text-sm font-medium text-white/80 dark:text-slate-300 mb-1">Your Progress</p>
                        <p className="text-2xl font-bold dark:text-white">{referrals} / {maxReferrals} Referrals</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-white/80 dark:text-slate-300 mb-1">Reward</p>
                        <p className="text-xl font-bold flex items-center gap-1 dark:text-white"><Gift className="w-5 h-5"/> Free Clean</p>
                      </div>
                    </div>
                    
                    <div className="relative pt-2">
                      <Progress value={progress} className="h-3 bg-white/20 dark:bg-slate-700 [&>div]:bg-white dark:[&>div]:bg-primary" />
                      <div className="flex justify-between mt-2 px-1">
                        {[1, 2, 3, 4, 5].map((step) => (
                          <div key={step} className="flex flex-col items-center">
                            <div className={`w-3 h-3 rounded-full ${step <= referrals ? 'bg-white dark:bg-primary' : 'bg-white/30 dark:bg-slate-700'} mb-1 transition-colors duration-500`} />
                            <span className="text-[10px] font-bold text-white/70 dark:text-slate-400">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-sm text-white/90 dark:text-slate-300 font-medium mt-4">
                      {maxReferrals - referrals} more to unlock a completely free standard clean!
                    </p>
                  </div>
                </div>
                {/* Decorative circles */}
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 dark:bg-primary/20 rounded-full blur-3xl"></div>
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 dark:bg-primary/20 rounded-full blur-3xl"></div>
              </div>
            </div>
          </Card>
          
          <div className="mt-6 md:mt-16 flex flex-col md:grid md:grid-cols-3 gap-3 md:gap-8">
            <div className="flex md:flex-col items-center md:items-start md:text-center text-left bg-white dark:bg-slate-800 p-4 md:p-6 rounded-xl md:rounded-2xl shadow-sm border dark:border-slate-700 gap-4 md:gap-0">
              <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 bg-primary/10 text-primary dark:text-primary rounded-full flex items-center justify-center md:mx-auto md:mb-4 font-bold text-lg md:text-xl">1</div>
              <div className="flex flex-col h-full justify-between w-full">
                <div>
                  <h3 className="font-bold text-base md:text-lg mb-0.5 md:mb-2 dark:text-white">Get Your Link</h3>
                  <p className="text-muted-foreground dark:text-slate-400 text-xs md:text-sm mb-3">Copy your unique referral link to share with friends.</p>
                </div>
                <Button size="sm" variant="secondary" onClick={handleCopyLink} className="w-full mt-auto md:mt-2 dark:text-white dark:bg-slate-700 dark:hover:bg-slate-600">
                  <Copy className="w-3 h-3 mr-2" /> Copy Link
                </Button>
              </div>
            </div>
            <div className="flex md:flex-col items-center md:items-start md:text-center text-left bg-white dark:bg-slate-800 p-4 md:p-6 rounded-xl md:rounded-2xl shadow-sm border dark:border-slate-700 gap-4 md:gap-0">
              <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 bg-primary/10 text-primary dark:text-primary rounded-full flex items-center justify-center md:mx-auto md:mb-4 font-bold text-lg md:text-xl">2</div>
              <div>
                <h3 className="font-bold text-base md:text-lg mb-0.5 md:mb-2 dark:text-white">Share with Friends</h3>
                <p className="text-muted-foreground dark:text-slate-400 text-xs md:text-sm">Send the link to friends. They get $25 off their first clean.</p>
              </div>
            </div>
            <div className="flex md:flex-col items-center md:items-start md:text-center text-left bg-white dark:bg-slate-800 p-4 md:p-6 rounded-xl md:rounded-2xl shadow-sm border dark:border-slate-700 gap-4 md:gap-0">
              <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 bg-primary/10 text-primary dark:text-primary rounded-full flex items-center justify-center md:mx-auto md:mb-4 font-bold text-lg md:text-xl">3</div>
              <div>
                <h3 className="font-bold text-base md:text-lg mb-0.5 md:mb-2 dark:text-white">Earn $25</h3>
                <p className="text-muted-foreground dark:text-slate-400 text-xs md:text-sm">When they complete their clean, you get $25 off your next service.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
